package org.example.project.enginepalettenkonto

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.engineservice.ServiceApi
import org.example.project.enginetabular.TABULAR_DOCUMENT_ICON_VALUE
import org.example.project.enginetabular.TabularScreen
import org.example.project.screens_cus.CUSCeoDashboardStyles
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.ZustandManager
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.utils.All
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.gridTemplateColumns
import org.jetbrains.compose.web.css.minWidth
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text

private const val PAL_DASHBOARD_PANEL_KONTO = "KONTO"
private const val PAL_DASHBOARD_PANEL_AUSGANG_MONAT = "AUSGANG_MONAT"
private const val PAL_DASHBOARD_PANEL_AUSGANG_GLOBAL = "AUSGANG_GLOBAL"
private const val PAL_DASHBOARD_PANEL_EINGANG_MONAT = "EINGANG_MONAT"
private const val PAL_DASHBOARD_PANEL_EINGANG_GLOBAL = "EINGANG_GLOBAL"
private const val PAL_DASHBOARD_PANEL_BEWEGUNGEN_GLOBAL = "BEWEGUNGEN_GLOBAL"
private const val PAL_DASHBOARD_PANEL_ABSCHLUESSE = "ABSCHLUESSE"

private val palDashboardBewegungHeaders = listOf(
    "Datum", "Tauschpartner", "Lieferscheinnummer", "Auftraggeber", "Empfänger", "Unternehmer", "Lieferant", "Palettentyp", "Vorgangstyp",
    "Eingang", "Ausgang", "Differenz", "Kontostand", "Palettenschein", "Zustand"
)

@Composable
fun PalDashboardScreen() {
    Style(CUSCeoDashboardStyles)
    val scope = rememberCoroutineScope()

    var loading by remember { mutableStateOf(true) }
    var selectedPanel by remember { mutableStateOf(PAL_DASHBOARD_PANEL_KONTO) }
    var partner by remember { mutableStateOf<List<All>>(emptyList()) }
    var kontoRows by remember { mutableStateOf<List<All>>(emptyList()) }
    var auskunftRows by remember { mutableStateOf<List<All>>(emptyList()) }
    var abschlussRows by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedAbschlussPartnerId by remember { mutableStateOf("") }
    var selectedAbschlussMonth by remember { mutableStateOf("") }
    var documentsByKey by remember { mutableStateOf<Map<String, List<All>>>(emptyMap()) }
    var searchedPanels by remember { mutableStateOf<Set<String>>(emptySet()) }
    val monthKey = remember { palDashboardCurrentMonthKey() }
    val refreshVersion = LocalScreenRefreshVersion.current

    suspend fun reload() {
        loading = true
        val firmenId = palDashboardCurrentFirmenId()
        val loadedPartner = PalettenkontoApi.loadPartner(firmenId)
        val loadedKonto = PalettenkontoApi.loadKonto(firmenId, "")
        val loadedAuskunft = PalettenkontoApi.loadAuskunft(firmenId, "")
        val loadedAbschluesse = PalettenkontoApi.loadAbschluesse(firmenId)
        partner = loadedPartner
        kontoRows = loadedKonto
        auskunftRows = loadedAuskunft.filter { palDashboardExtra(it, "source") == "system" }
        abschlussRows = loadedAbschluesse
        if (selectedAbschlussPartnerId.isBlank()) {
            selectedAbschlussPartnerId = loadedAbschluesse.firstOrNull()?.kundenId.orEmpty()
                .ifBlank { loadedPartner.firstOrNull()?.id.orEmpty() }
        }
        if (selectedAbschlussMonth.isBlank()) {
            selectedAbschlussMonth = loadedAbschluesse.firstOrNull { palDashboardExtra(it, "kundenId") == selectedAbschlussPartnerId }?.datum.orEmpty()
                .ifBlank { loadedAbschluesse.firstOrNull()?.datum.orEmpty() }
        }
        documentsByKey = palDashboardLoadDocuments(auskunftRows + loadedAbschluesse.flatMap { it.verbrauch })
        loading = false
    }

    LaunchedEffect(refreshVersion) { reload() }

    fun searchDashboard(filters: Map<String, String>) {
        scope.launch {
            searchedPanels = searchedPanels + selectedPanel
            when (selectedPanel) {
                PAL_DASHBOARD_PANEL_KONTO -> {
                    partner = PalettenkontoApi.searchDashboard("konto", filters)
                    kontoRows = emptyList()
                }
                PAL_DASHBOARD_PANEL_ABSCHLUESSE -> {
                    val filtered = PalettenkontoApi.searchDashboard(
                        "abschluss",
                        filters,
                        mapOf("partnerId" to selectedAbschlussPartnerId, "month" to selectedAbschlussMonth)
                    )
                    abschlussRows = abschlussRows.map { row ->
                        if (palDashboardExtra(row, "kundenId") == selectedAbschlussPartnerId && row.datum == selectedAbschlussMonth) {
                            row.copy(verbrauch = filtered)
                        } else row
                    }
                }
                else -> {
                    val monthly = selectedPanel in setOf(PAL_DASHBOARD_PANEL_AUSGANG_MONAT, PAL_DASHBOARD_PANEL_EINGANG_MONAT)
                    val direction = when (selectedPanel) {
                        PAL_DASHBOARD_PANEL_AUSGANG_MONAT, PAL_DASHBOARD_PANEL_AUSGANG_GLOBAL -> "ausgang"
                        PAL_DASHBOARD_PANEL_EINGANG_MONAT, PAL_DASHBOARD_PANEL_EINGANG_GLOBAL -> "eingang"
                        else -> ""
                    }
                    auskunftRows = PalettenkontoApi.searchDashboard(
                        "bewegungen",
                        filters,
                        mapOf("month" to if (monthly) monthKey else "", "direction" to direction)
                    )
                }
            }
        }
    }

    val monthRows = auskunftRows.filter { palDashboardMonthKey(it) == monthKey }
    val ausgangMonatRows = monthRows.filter { palDashboardAusgang(it) > 0 }
    val ausgangGlobalRows = auskunftRows.filter { palDashboardAusgang(it) > 0 }
    val eingangMonatRows = monthRows.filter { palDashboardEingang(it) > 0 }
    val eingangGlobalRows = auskunftRows.filter { palDashboardEingang(it) > 0 }
    val tiles = listOf(
        palDashboardTile(PAL_DASHBOARD_PANEL_KONTO, "Tauschpartner", "images/palauskunft.svg", partner.size.toString()),
        palDashboardTile(PAL_DASHBOARD_PANEL_AUSGANG_MONAT, "Pal Ausgang Monat", "images/wa.svg", ausgangMonatRows.sumOf { palDashboardAusgang(it) }.toString()),
        palDashboardTile(PAL_DASHBOARD_PANEL_AUSGANG_GLOBAL, "Pal Ausgang Global", "images/wa.svg", ausgangGlobalRows.sumOf { palDashboardAusgang(it) }.toString()),
        palDashboardTile(PAL_DASHBOARD_PANEL_EINGANG_MONAT, "Pal Eingang Monat", "images/we.svg", eingangMonatRows.sumOf { palDashboardEingang(it) }.toString()),
        palDashboardTile(PAL_DASHBOARD_PANEL_EINGANG_GLOBAL, "Pal Eingang Global", "images/we.svg", eingangGlobalRows.sumOf { palDashboardEingang(it) }.toString()),
        palDashboardTile(PAL_DASHBOARD_PANEL_BEWEGUNGEN_GLOBAL, "Bewegungen Global", "images/order.svg", auskunftRows.sumOf { palDashboardEingang(it) + palDashboardAusgang(it) }.toString()),
        palDashboardTile(PAL_DASHBOARD_PANEL_ABSCHLUESSE, "Monatsabschlüsse", "images/pdf.svg", abschlussRows.size.toString())
    )
    val selectedAbschluss = abschlussRows.firstOrNull {
        palDashboardExtra(it, "kundenId") == selectedAbschlussPartnerId && it.datum == selectedAbschlussMonth
    }

    Div({ classes(CUSCeoDashboardStyles.screenRoot) }) {
        Div({ classes(CUSCeoDashboardStyles.stickyTop) }) {
            if (selectedPanel == PAL_DASHBOARD_PANEL_ABSCHLUESSE) {
                PalDashboardAbschlussFilter(
                    partner = partner,
                    abschluesse = abschlussRows,
                    selectedPartnerId = selectedAbschlussPartnerId,
                    selectedMonth = selectedAbschlussMonth,
                    onPartnerChange = {
                        selectedAbschlussPartnerId = it
                        selectedAbschlussMonth = abschlussRows.firstOrNull { row -> palDashboardExtra(row, "kundenId") == it }?.datum.orEmpty()
                    },
                    onMonthChange = { selectedAbschlussMonth = it }
                )
            }
            Div({ classes(CUSCeoDashboardStyles.tileScrollHost) }) {
                Div({
                    classes(CUSCeoDashboardStyles.grid)
                    style {
                        gridTemplateColumns("repeat(7, 240px)")
                        property("width", "max-content")
                        property("margin-left", "auto")
                        property("margin-right", "auto")
                    }
                }) {
                    tiles.forEach { tile ->
                        PalDashboardTile(
                            tile = tile,
                            loading = loading,
                            active = selectedPanel == tile.key,
                            onClick = { selectedPanel = tile.key }
                        )
                    }
                }
            }
        }

        Div({ classes(CUSCeoDashboardStyles.tabularArea) }) {
            when (selectedPanel) {
                PAL_DASHBOARD_PANEL_AUSGANG_MONAT -> PalDashboardBewegungenTable("Pal Ausgang Monat", if (selectedPanel in searchedPanels) ausgangMonatRows else emptyList(), documentsByKey, ::searchDashboard)
                PAL_DASHBOARD_PANEL_AUSGANG_GLOBAL -> PalDashboardBewegungenTable("Pal Ausgang Global", if (selectedPanel in searchedPanels) ausgangGlobalRows else emptyList(), documentsByKey, ::searchDashboard)
                PAL_DASHBOARD_PANEL_EINGANG_MONAT -> PalDashboardBewegungenTable("Pal Eingang Monat", if (selectedPanel in searchedPanels) eingangMonatRows else emptyList(), documentsByKey, ::searchDashboard)
                PAL_DASHBOARD_PANEL_EINGANG_GLOBAL -> PalDashboardBewegungenTable("Pal Eingang Global", if (selectedPanel in searchedPanels) eingangGlobalRows else emptyList(), documentsByKey, ::searchDashboard)
                PAL_DASHBOARD_PANEL_BEWEGUNGEN_GLOBAL -> PalDashboardBewegungenTable("Palettenbewegungen Global", if (selectedPanel in searchedPanels) auskunftRows else emptyList(), documentsByKey, ::searchDashboard)
                PAL_DASHBOARD_PANEL_ABSCHLUESSE -> PalDashboardAbschlussTable(if (selectedPanel in searchedPanels) selectedAbschluss else null, documentsByKey, ::searchDashboard)
                else -> PalDashboardKontoTable(if (selectedPanel in searchedPanels) kontoRows else emptyList(), if (selectedPanel in searchedPanels) partner else emptyList(), if (selectedPanel in searchedPanels) auskunftRows else emptyList(), ::searchDashboard)
            }
        }
    }
}

@Composable
private fun PalDashboardTile(tile: All, loading: Boolean, active: Boolean, onClick: () -> Unit) {
    Div({
        if (active) {
            classes(CUSCeoDashboardStyles.tile, CUSCeoDashboardStyles.tileClickable, CUSCeoDashboardStyles.tileActive)
        } else {
            classes(CUSCeoDashboardStyles.tile, CUSCeoDashboardStyles.tileClickable)
        }
        onClick { onClick() }
    }) {
        Div({ classes(CUSCeoDashboardStyles.iconSlot) }) {
            Img(src = tile.icon, attrs = { classes(CUSCeoDashboardStyles.icon) })
        }
        Div({ classes(CUSCeoDashboardStyles.textColumn) }) {
            Div({ classes(CUSCeoDashboardStyles.tileLabel) }) { Text(tile.label) }
            Div({ classes(CUSCeoDashboardStyles.value) }) {
                Text(if (loading) "..." else tile.bezeichnung)
            }
        }
    }
}

@Composable
private fun PalDashboardAbschlussFilter(
    partner: List<All>,
    abschluesse: List<All>,
    selectedPartnerId: String,
    selectedMonth: String,
    onPartnerChange: (String) -> Unit,
    onMonthChange: (String) -> Unit
) {
    val partnerIdsWithAbschluss = abschluesse.map { palDashboardExtra(it, "kundenId") }.filter { it.isNotBlank() }.toSet()
    val partnerOptions = partner
        .filter { it.id in partnerIdsWithAbschluss }
        .map { All(key = it.id, label = it.firma.ifBlank { it.kunde.ifBlank { it.id } }) }
        .ifEmpty {
            abschluesse
                .map { All(key = palDashboardExtra(it, "kundenId"), label = palDashboardExtra(it, "tauschpartner").ifBlank { it.kunde.ifBlank { it.firma } }) }
                .filter { it.key.isNotBlank() }
                .distinctBy { it.key }
        }
    val monthOptions = abschluesse
        .filter { selectedPartnerId.isBlank() || palDashboardExtra(it, "kundenId") == selectedPartnerId }
        .map { All(key = it.datum, label = palDashboardMonthLabel(it.datum)) }
        .distinctBy { it.key }

    Div({
        style {
            display(DisplayStyle.Flex)
            property("justify-content", "center")
            property("gap", "16px")
            property("margin-bottom", "12px")
        }
    }) {
        Div({
            style {
                minWidth(260.px)
                property("width", "auto")
                property("max-width", "420px")
            }
        }) {
            AppDropdown(
                options = partnerOptions,
                selectedValue = selectedPartnerId,
                onChange = onPartnerChange,
                placeholder = "Tauschpartner",
                appearance = AppDropdownAppearance.Underline,
                menuMode = AppDropdownMenuMode.Overlay
            )
        }
        Div({
            style {
                minWidth(180.px)
                property("width", "auto")
                property("max-width", "260px")
            }
        }) {
            AppDropdown(
                options = monthOptions,
                selectedValue = selectedMonth,
                onChange = onMonthChange,
                placeholder = "Monat",
                appearance = AppDropdownAppearance.Underline,
                menuMode = AppDropdownMenuMode.Overlay
            )
        }
    }
}

@Composable
private fun PalDashboardKontoTable(rows: List<All>, partner: List<All>, movements: List<All>, onSearch: (Map<String, String>) -> Unit) {
    val headers = listOf("Firma", "Tauschart", "Kontostand", "Buchungstag", "Ansprechpartner", "Email", "Telefon", "Mobil")
    val saldoByPartnerId = rows
        .groupBy { palDashboardExtra(it, "kundenId").ifBlank { it.kunde } }
        .mapValues { (_, values) -> values.sumOf { it.palettenanzahl } }
    val tauschartByPartnerId = movements
        .mapNotNull { row ->
            val partnerId = palDashboardExtra(row, "kundenId")
            val tauschart = palDashboardExtra(row, "tauschart")
            if (partnerId.isNotBlank() && tauschart.isNotBlank()) partnerId to tauschart else null
        }
        .toMap()
    PalDashboardTabularScreen(
        screenKey = "PALDashboard_Konto",
        title = "Palettenkonto",
        headers = headers,
        data = partner.map { row ->
            row.id to listOf(
                row.firma,
                palDashboardExtra(row, "tauschart").ifBlank { tauschartByPartnerId[row.id].orEmpty() },
                palDashboardSigned((saldoByPartnerId[row.id] ?: row.palettenanzahl).toString()),
                palDashboardExtra(row, "abschlusstag"),
                row.ansprechpartner,
                row.email,
                row.telefon,
                row.mobil
            )
        },
        onSearch = onSearch
    )
}

@Composable
private fun PalDashboardAbschlussTable(
    abschluss: All?,
    documentsByKey: Map<String, List<All>>,
    onSearch: (Map<String, String>) -> Unit
) {
    val scope = rememberCoroutineScope()
    PalDashboardTabularScreen(
        screenKey = "PALDashboard_Monatsabschluesse",
        title = "Monatsabschlüsse",
        headers = palDashboardBewegungHeaders,
        data = abschluss?.verbrauch.orEmpty()
            .sortedWith(compareByDescending<All> { palDashboardSortableDate(it.datum) }.thenByDescending { it.lieferscheinnummer })
            .map { row -> row.id to palDashboardBewegungRow(row, documentsByKey) },
        onCellClick = { rowId, colIndex ->
            val rows = abschluss?.verbrauch.orEmpty()
            val kind = palDashboardDocumentKind(colIndex)
            if (kind.isNotBlank()) scope.launch { palDashboardOpenDocument(rows, documentsByKey, rowId, kind) }
        },
        onSearch = onSearch
    )
}

@Composable
private fun PalDashboardBewegungenTable(
    title: String,
    rows: List<All>,
    documentsByKey: Map<String, List<All>>,
    onSearch: (Map<String, String>) -> Unit
) {
    val scope = rememberCoroutineScope()
    PalDashboardTabularScreen(
        screenKey = "PALDashboard_${title.filter { it.isLetterOrDigit() }}",
        title = title,
        headers = palDashboardBewegungHeaders,
        data = rows.sortedWith(compareByDescending<All> { palDashboardSortableDate(it.datum) }.thenByDescending { it.lieferscheinnummer })
            .map { row -> row.id to palDashboardBewegungRow(row, documentsByKey) },
        onCellClick = { rowId, colIndex ->
            val kind = palDashboardDocumentKind(colIndex)
            if (kind.isNotBlank()) scope.launch { palDashboardOpenDocument(rows, documentsByKey, rowId, kind) }
        },
        onSearch = onSearch
    )
}

@Composable
private fun PalDashboardTabularScreen(
    screenKey: String,
    title: String,
    headers: List<String>,
    data: List<Pair<String, List<String>>>,
    onCellClick: ((String, Int) -> Unit)? = null,
    onSearch: (Map<String, String>) -> Unit
) {
    Div({ classes(CUSCeoDashboardStyles.verbrauchTable) }) {
        TabularScreen(
            screenKey = screenKey,
            rollenKey = "PALDashboard",
            title = title,
            headers = headers,
            data = data,
            editable = false,
            hardReadOnly = true,
            includeMetadata = false,
            nonEditableCols = headers.toSet(),
            allowCreate = false,
            allowDelete = false,
            allowSave = false,
            allowImportButton = false,
            allowExportButton = true,
            allowRefreshButton = true,
            allowSearchButton = true,
            onSearchWithFilters = onSearch,
            allowSettingsButton = true,
            useUserSettings = false,
            smartMode = true,
            onCellClick = onCellClick,
            onCommit = {}
        )
    }
}

private fun palDashboardBewegungRow(row: All, documentsByKey: Map<String, List<All>>): List<String> =
    listOf(
        row.datum,
        palDashboardTauschpartner(row),
        row.lieferscheinnummer,
        row.auftraggeber,
        row.empfaenger,
        row.unternehmer,
        row.lieferant,
        palDashboardExtra(row, "palettentyp"),
        palDashboardVorgangstypLabel(row),
        palDashboardExtra(row, "eingang"),
        palDashboardExtra(row, "ausgang"),
        palDashboardDifferenz(row),
        palDashboardSigned(palDashboardExtra(row, "saldo")),
        palDashboardDocumentIcon(row, documentsByKey, "palettenschein"),
        ZustandManager.textFor("PAL", row.zustand)
    )

private fun palDashboardTile(key: String, label: String, icon: String, value: String): All =
    All(key = key, label = label, icon = icon, bezeichnung = value)

private fun palDashboardCurrentFirmenId(): String =
    LoginService.getUser()?.firmenId.orEmpty().trim().ifBlank { GlobalManager.currentFirmenId.trim() }

private fun palDashboardExtra(row: All, key: String): String =
    when (key) {
        "source" -> row.source
        "kundenId" -> row.kundenId
        "tauschpartner" -> row.tauschpartner
        "palettentyp" -> row.palettentyp
        "vorgangstyp" -> row.vorgangstyp.ifBlank { row.auftragsart }
        "eingang" -> row.eingang
        "ausgang" -> row.ausgang
        "saldo" -> row.saldo
        "palKontoDifferenz" -> row.palKontoDifferenz
        "monat" -> row.monat
        "gebuchtAm" -> row.gebuchtAm
        "tauschart" -> row.tauschart
        "abschlusstag" -> row.abschlusstag
        else -> row.palettenWerte[key].orEmpty()
    }

private fun palDashboardTauschpartner(row: All): String =
    palDashboardExtra(row, "tauschpartner").ifBlank { row.kunde }.ifBlank { row.firma }

private fun palDashboardEingang(row: All): Int =
    palDashboardExtra(row, "eingang").toIntOrNull() ?: 0

private fun palDashboardAusgang(row: All): Int =
    palDashboardExtra(row, "ausgang").toIntOrNull() ?: 0

private fun palDashboardSigned(value: String): String {
    val number = value.toIntOrNull() ?: 0
    return if (number >= 0) "+$number" else number.toString()
}

private fun palDashboardDifferenz(row: All): String {
    palDashboardExtra(row, "palKontoDifferenz").takeIf { it.isNotBlank() }?.let { return palDashboardSigned(it) }
    return palDashboardSigned((palDashboardEingang(row) - palDashboardAusgang(row)).toString())
}

private fun palDashboardVorgangstypLabel(row: All): String =
    when (palDashboardExtra(row, "vorgangstyp").uppercase()) {
        "WE" -> "Liefern"
        "WA" -> "Laden"
        else -> palDashboardExtra(row, "vorgangstyp")
    }

private fun palDashboardMonthKey(row: All): String =
    palDashboardExtra(row, "monat")
        .ifBlank { palDashboardMonthKeyFromText(row.datum) }
        .ifBlank { palDashboardMonthKeyFromText(palDashboardExtra(row, "gebuchtAm")) }

private fun palDashboardMonthKeyFromText(value: String): String {
    val text = value.trim()
    if (text.length >= 7 && text[4] == '_') return text.take(7)
    if (text.length >= 7 && text[4] == '-') return text.take(7).replace("-", "_")
    val parts = text.split(".", "/", "-")
    return when {
        parts.size >= 3 && parts[2].length == 4 -> "${parts[2]}_${parts[1].padStart(2, '0')}"
        parts.size >= 2 && parts[0].length == 4 -> "${parts[0]}_${parts[1].padStart(2, '0')}"
        else -> ""
    }
}

private fun palDashboardSortableDate(value: String): String {
    val parts = value.trim().split(".", "/", "-")
    return when {
        parts.size >= 3 && parts[2].length == 4 -> "${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}"
        parts.size >= 3 && parts[0].length == 4 -> "${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}"
        else -> value
    }
}

private fun palDashboardCurrentMonthKey(): String {
    val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
    return "${now.year}_${now.monthNumber.toString().padStart(2, '0')}"
}

private fun palDashboardMonthLabel(monthKey: String): String {
    val year = monthKey.substringBefore("_").toIntOrNull()
    val month = monthKey.substringAfter("_", "").toIntOrNull()
    if (year == null || month == null) return monthKey
    val name = when (month) {
        1 -> "Januar"
        2 -> "Februar"
        3 -> "März"
        4 -> "April"
        5 -> "Mai"
        6 -> "Juni"
        7 -> "Juli"
        8 -> "August"
        9 -> "September"
        10 -> "Oktober"
        11 -> "November"
        12 -> "Dezember"
        else -> month.toString()
    }
    return "$name $year"
}

private suspend fun palDashboardLoadDocuments(rows: List<All>): Map<String, List<All>> {
    val result = mutableMapOf<String, List<All>>()
    rows
        .filter { it.lieferscheinnummer.isNotBlank() }
        .map { palDashboardDocumentListKey(it) to it }
        .distinctBy { it.first }
        .forEach { (key, row) ->
            result[key] = runCatching { ServiceApi.serviceDocuments(palDashboardServiceType(row), row.lieferscheinnummer) }.getOrDefault(emptyList())
        }
    return result
}

private fun palDashboardDocumentKind(colIndex: Int): String =
    when (palDashboardBewegungHeaders.getOrNull(colIndex)) {
        "Palettenschein" -> "palettenschein"
        else -> ""
    }

private fun palDashboardDocumentIcon(row: All, documentsByKey: Map<String, List<All>>, kind: String): String =
    if (palDashboardDocument(row, documentsByKey, kind) != null) TABULAR_DOCUMENT_ICON_VALUE else ""

private suspend fun palDashboardOpenDocument(rows: List<All>, documentsByKey: Map<String, List<All>>, rowId: String, kind: String) {
    val row = rows.firstOrNull { it.id == rowId } ?: return
    val doc = palDashboardDocument(row, documentsByKey, kind) ?: return
    ServiceApi.openServiceDocumentMeta(palDashboardServiceType(row), row.lieferscheinnummer, doc)
}

private fun palDashboardDocument(row: All, documentsByKey: Map<String, List<All>>, kind: String): All? {
    val expected = palDashboardExpectedFileName(row, kind).lowercase()
    if (expected.isBlank()) return null
    return documentsByKey[palDashboardDocumentListKey(row)]
        .orEmpty()
        .firstOrNull { doc ->
            val file = doc.file.ifBlank { "${doc.name}.pdf" }.lowercase()
            file == expected || file.substringBeforeLast(".") == expected.substringBeforeLast(".")
        }
        ?: documentsByKey[palDashboardDocumentListKey(row)]
            .orEmpty()
            .firstOrNull { doc ->
                val file = doc.file.ifBlank { "${doc.name}.pdf" }.lowercase()
                val prefix = "${row.lieferscheinnummer}-${palDashboardLkwDocumentId(row)}".lowercase()
                file.startsWith(prefix) && when (kind) {
                    "palettenschein" -> file.contains("palettenschein")
                    "prozess" -> file.contains("prozessdokumentation")
                    "qualitaet" -> file.contains("qualitaetsdokumentation") || file.contains("qualitätsdokumentation")
                    "lieferschein" -> file.contains("lieferschein")
                    else -> false
                }
            }
}

private fun palDashboardExpectedFileName(row: All, kind: String): String {
    val ls = row.lieferscheinnummer
    val lkw = palDashboardLkwDocumentId(row)
    if (ls.isBlank() || lkw.isBlank()) return ""
    val standort = row.originalStandortKey
        .ifBlank { row.waStandortId }
        .ifBlank { row.slotStandortId }
        .ifBlank { row.slotYardStandortKey }
        .takeIf { it.isNotBlank() }
        ?.let { "-standort-${it.filter { ch -> ch.isLetterOrDigit() || ch == '-' || ch == '_' }}" }
        .orEmpty()
    return when (kind) {
        "palettenschein" -> "${ls}-${lkw}${standort}_palettenschein.pdf"
        "prozess" -> "${ls}-${lkw}${standort}_prozessdokumentation.pdf"
        "qualitaet" -> "${ls}_${lkw}${standort.replace("-", "_")}_qualitaetsdokumentation.pdf"
        "lieferschein" -> "${ls}-${lkw}_lieferschein.pdf"
        else -> ""
    }
}

private fun palDashboardLkwDocumentId(row: All): String =
    row.originalLfdNr
        .takeIf { it.toIntOrNull()?.let { number -> number > 0 } == true }
        ?: row.originalId

private fun palDashboardDocumentListKey(row: All): String =
    "${palDashboardServiceType(row)}:${row.lieferscheinnummer}"

private fun palDashboardServiceType(row: All): String =
    if (palDashboardExtra(row, "vorgangstyp").uppercase() == "WA") "yard-wa" else "yard-we"
